import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import IntlTelInput from 'intl-tel-input/angularWithUtils';
import { Subject } from 'rxjs';

let nextUniqueId = 0;

@Component({
  selector: 'app-phone-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IntlTelInput, MatFormFieldModule, MatInputModule],
  templateUrl: './phone-field.html',
  styleUrls: ['./phone-field.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneField),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneField),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class PhoneField implements ControlValueAccessor, Validator, AfterViewInit, OnDestroy {
  @ViewChild('intlTelInput') telInput: IntlTelInput | undefined;

  loadUtils = () => import('intl-tel-input/utils');
  stateChanges = new Subject<void>();
  innerControl = new FormControl<string | null>(null);
  hasValue = false;

  focused = false;
  touched = false;
  id = `app-phone-field-${nextUniqueId++}`;
  @Input() label?: string;
  @Input() separateDialCode = false;
  @Input() strictMode = true;
  @Input() placeholder = 'Téléphone';
  @Input() required = false;

  private onChange: (value: string | null) => void = () => {
    // No-op
  };

  private onTouched: () => void = () => {
    // No-op
  };

  private onValidatorChange: () => void = () => {
    // No-op
  };

  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private errorByCode: Record<number, string> = {};

  constructor() {
    this.innerControl.valueChanges.subscribe((value) => {
      this.hasValue = !!value?.length;
      this.onChange(value);
      this.stateChanges.next();
      this.onValidatorChange();
      if (value) {
        // Force la validation du numéro de téléphone et la mise à jour de l'erreur
        // y compris si remplissage via autofill du navigateur
        this.telInput?.getInstance()?.setNumber(value);
        this.cdr.markForCheck();
      }
    });
  }

  get errorCode(): number | null {
    return this.telInput?.getInstance()?.getValidationError() ?? null;
  }

  get errorMessage(): string | null {
    if (this.errorCode !== null && this.errorCode !== 0) {
      return this.errorByCode[this.errorCode] ?? 'Numéro de téléphone invalide';
    }
    return null;
  }

  get isRequired(): boolean {
    if (this.required !== undefined && this.required !== null) {
      if (typeof this.required === 'boolean') {
        return this.required;
      }
      return this.required === 'true';
    }

    return this.innerControl?.hasValidator(Validators.required) ?? false;
  }

  writeValue(value: string | null): void {
    this.innerControl.setValue(value, { emitEvent: false });
    this.hasValue = !!value?.length;
    this.stateChanges.next();
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.checkNativeInputValue();
      });
    });
    const input = this.telInput?.getInput();
    input?.addEventListener('mouseenter', () => this.checkNativeInputValue());
    input?.addEventListener('input', () => this.checkNativeInputValue());
    this.loadUtils().then((module) => {
      this.innerControl.markAsPristine();
      const utils = module.default;
      console.log('intl-tel-input utils loaded', { validationError: utils.validationError });
      const { validationError } = utils;
      this.errorByCode = {
        [validationError.IS_POSSIBLE]: 'Le numéro semble valide',
        [validationError.IS_POSSIBLE_LOCAL_ONLY]: 'Le numéro est valide seulement localement',
        [validationError.INVALID_COUNTRY_CODE]: 'Le code pays est invalide',
        [validationError.TOO_SHORT]: 'Le numéro est trop court',
        [validationError.TOO_LONG]: 'Le numéro est trop long',
        [validationError.INVALID_LENGTH]: 'La longueur du numéro est invalide',
      };
    });
  }

  private checkNativeInputValue(): void {
    const inputElement = this.telInput?.getInput();
    if (inputElement instanceof HTMLInputElement) {
      const hasNativeValue = inputElement.value?.length > 0;
      if (hasNativeValue && !this.hasValue) {
        this.hasValue = true;
        this.stateChanges.next();
      }
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.innerControl.disable({ emitEvent: false });
    } else {
      this.innerControl.enable({ emitEvent: false });
    }
    this.stateChanges.next();
  }

  validate(): ValidationErrors | null {
    if (this.required && !this.innerControl.value) {
      return { required: true };
    }
    return this.innerControl.errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(): void {
    if (this.focused) {
      this.focused = false;
      this.touched = true;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }
}
